import React, { useRef, useState } from 'react';
import { FormControl, FormHelperText, MenuItem, Menu, InputAdornment, IconButton, TextField, FormLabel, Typography, styled, alpha, Box, Stack, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import { useTranslation } from 'react-i18next';
import { Info } from '@mui/icons-material';

const CustomTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        '& fieldset': {
            borderColor: '#D0D5DD',
        },
        '&:hover fieldset': {
            borderColor: '#D0D5DD',
        },
        '&.Mui-focused fieldset': {
            boxShadow: `${alpha(theme.palette.primary.main, 0.1)} 0 0 0 0.2rem`,
            borderColor: theme.palette.primary.main,
            borderWidth: '1px',
        },
    },
    '& .MuiInputBase-input': {
        fontSize: 16,
        fontStyle: 'normal',
        fontWeight: 400,
        paddingTop: 10,
        paddingBottom: 10,
    },
}));

export default function MapitFormSelect({
    name,
    value,
    items,
    error,
    placeholder,
    label,
    onChange,
    optional = false,
    ...props
}) {
    const { t } = useTranslation("auth");
    const [anchorEl, setAnchorEl] = useState(null);

    const textFieldRef = useRef(null);

    const handleClick = (event) => {
        event.stopPropagation();
        setAnchorEl(anchorEl ? null : textFieldRef.current);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const open = Boolean(anchorEl);

    return (
        <Box display="flex" flexDirection="column">
            <Typography variant='caption'
                sx={{
                    color: '#101828 !important',
                    fontSize: " 12.5px",
                    mb: 1.1
                }}
            >
                {label}
                {optional &&
                    <span
                        style={{
                            marginRight: "7px",
                            marginLeft: "7px",
                            color: "#98A2B3",
                            fontSize: "14px",
                            fontWeight: "400",
                            lineHeight: "20px",
                        }}>
                        {`(${t("form.optional")})`}
                    </span>
                }
                {props?.required && <span>{" *"}</span>}
            </Typography>
            <CustomTextField
                readOnly
                size="small"
                ref={textFieldRef}
                onClick={handleClick}
                id={name}
                aria-controls={open ? name : undefined}
                aria-haspopup="true"
                aria-expanded={open ? 'true' : undefined}
                InputLabelProps={{
                    required: false,
                    sx: {
                        color: "text.placeholder",
                    },
                    shrink: Boolean(value)
                }}
                InputProps={{
                    endAdornment: (
                        <InputAdornment position="end">
                            <IconButton
                                onClick={handleClick}
                                edge="end"
                            >
                                {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
                            </IconButton>
                        </InputAdornment>
                    ),
                }}
                variant="outlined"
                placeholder={placeholder ?? ''}
                value={items?.find((item) => item?.value === value)?.label ?? ''}
                // error={!!error}
                helperText={error ?? ''}
                FormHelperTextProps={{
                    sx: {
                        mx: 0,
                        color: "error.main",
                    }
                }}
                {...props}
            />
            <Menu
                id={name}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    'aria-labelledby': 'basic-button',
                }}
                PaperProps={{
                    style: {
                        width: anchorEl ? anchorEl?.clientWidth : textFieldRef?.current?.clientWidth,
                    },
                }}
            >
                {items.map((item) => (
                    <MenuItem
                        key={item?.value}
                        selected={item?.value === value}
                        onClick={() => {
                            onChange({ target: { name: name, value: item?.value } })
                            handleClose()
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={0.5}>
                            {!!item.subLabel &&
                                <Tooltip title={item.subLabel} placement="top">
                                    <Info color='primary' fontSize='small' />
                                </Tooltip>
                            }
                            {item.label}
                        </Box>
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
}